import { ChangeDetectorRef, Component, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ReportQuery, ReportQueryService } from '../../../services/report-query';
import { dashboardErrorMessage, dashboardLayoutStyle } from '../../widget-and-graphs.models';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';

type MoneyMode = 'full' | 'cash' | 'bank' | 'movement';
interface MoneyRow { key: string; detail: string; balance: number; inbound: number; outbound: number; date?: string; }
interface MoneyGroup { key: string; label: string; rows: MoneyRow[]; total: number; }

@Component({
  selector: 'app-dashboard-finance-money-widget',
  standalone: false,
  templateUrl: './dashboard-finance-money-widget.html',
  styleUrl: './dashboard-finance-money-widget.scss',
})
export class DashboardFinanceMoneyWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input() title = 'Money position';
  @Input() mode: MoneyMode = 'full';
  @Input() bankAccountId?: string;
  @Input() accountName?: string;
  @Input() availableBalance?: number | string | null;
  @Input() currentBalance?: number | string | null;
  @Input() currencyCode = 'TZS';
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 8 } };

  readonly today = dateOnly(new Date());
  asOfDate = this.today;
  loading = false;
  error = '';
  rows: MoneyRow[] = [];
  private generation = 0;

  constructor(private readonly reports: ReportQueryService, private readonly cdr: ChangeDetectorRef) {}

  @HostBinding('attr.style') get layoutStyles(): string { return dashboardLayoutStyle(this.layout); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mode'] || changes['bankAccountId']) void this.load();
  }

  ngOnDestroy(): void { this.generation += 1; }

  onDateChange(value: string): void {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || value > this.today) {
      this.error = 'Choose a valid date on or before today.';
      return;
    }
    this.asOfDate = value;
    void this.load();
  }

  get isMovement(): boolean { return this.mode === 'movement'; }
  get isBank(): boolean { return this.mode === 'bank' || this.mode === 'movement'; }

  get groups(): MoneyGroup[] {
    const visible = this.mode === 'cash'
      ? this.rows.filter(row => row.key.startsWith('cash_') || row.key === 'asset_receivable' || row.key === 'liability_payable' || row.key.startsWith('tax_'))
      : this.rows;
    const grouped = new Map<string, MoneyGroup>();
    for (const row of visible) {
      const current = grouped.get(row.key) ?? { key: row.key, label: classLabel(row.key), rows: [], total: 0 };
      current.rows.push(row);
      current.total += row.balance;
      grouped.set(row.key, current);
    }
    return [...grouped.values()].sort((a, b) => a.label.localeCompare(b.label));
  }

  get cashHeld(): number { return this.sumClass(row => row.key.startsWith('cash_')); }
  get receivable(): number { return this.sumClass(row => row.key === 'asset_receivable'); }
  get payable(): number { return -this.sumClass(row => row.key === 'liability_payable'); }
  get assets(): number { return this.sumClass(row => row.key.startsWith('asset_') || row.key.startsWith('cash_') || row.key.startsWith('tax_asset_')); }
  get liabilities(): number { return -this.sumClass(row => row.key.startsWith('liability_') || row.key.startsWith('tax_liability_')); }
  get netAssets(): number { return this.assets - this.liabilities; }
  get cashNet(): number { return this.cashHeld + this.receivable - this.payable + this.sumClass(row => row.key.startsWith('tax_')); }
  get inbound(): number { return this.rows.reduce((sum, row) => sum + (row.key.startsWith('cash_') ? row.inbound : 0), 0); }
  get outbound(): number { return this.rows.reduce((sum, row) => sum + (row.key.startsWith('cash_') ? row.outbound : 0), 0); }

  money(value: number | string | null | undefined): string {
    const amount = Number(value ?? 0);
    return new Intl.NumberFormat('en-TZ', { style: 'currency', currency: this.currencyCode || 'TZS', maximumFractionDigits: 2 }).format(Number.isFinite(amount) ? amount : 0);
  }

  describeClass(value: string): string {
    const descriptions: Record<string, string> = {
      cash_bank: 'Money recorded in this bank account’s posted journal.',
      cash_branch: 'Physical cash recorded for this branch.',
      asset_receivable: 'Money customers still owe the company.',
      liability_payable: 'Money the company still owes suppliers.',
      asset_fixed: 'Property and equipment owned for long-term use.',
      asset_current: 'Stock and other resources expected to turn into cash soon.',
      asset_non_current: 'Other long-term resources owned by the company.',
      asset_prepayments: 'Costs paid ahead of receiving goods or services.',
      liability_current: 'Other amounts due in the near term.',
      liability_non_current: 'Amounts owed over a longer period.',
      liability_credit_card: 'Amounts owed on company credit cards.',
      equity: 'Owners’ stake after company debts.',
      equity_unaffected: 'Current-year earnings carried in equity.',
      income: 'Money earned from normal sales.',
      income_other: 'Money earned from other sources.',
      expense: 'Costs of running the company.',
      expense_direct_cost: 'Costs directly tied to goods or services sold.',
      expense_depreciation: 'The cost of long-lived assets spread over time.',
      off_balance: 'Commitments tracked outside the balance sheet.',
    };
    if (value.startsWith('tax_')) return 'Tax recorded in this account; compare its signed balance with the tax report.';
    return descriptions[value] ?? 'See the named account and journal for this posted balance.';
  }

  private sumClass(predicate: (row: MoneyRow) => boolean): number {
    return this.rows.reduce((sum, row) => sum + (predicate(row) ? row.balance : 0), 0);
  }

  private async load(): Promise<void> {
    const request = ++this.generation;
    this.rows = [];
    this.error = '';
    if (this.isBank && !this.bankAccountId) return;
    this.loading = true;
    this.cdr.detectChanges();
    try {
      const query = this.query();
      await firstValueFrom(this.reports.verifyOutput(query, 'spreadsheet'));
      const rows: MoneyRow[] = [];
      const pageSize = 200;
      for (let offset = 0; offset < 50000; offset += pageSize) {
        const result = await firstValueFrom(this.reports.execute({ ...query, offset }));
        if (request !== this.generation) return;
        for (const raw of result.rows) {
          rows.push({
            key: this.isMovement ? 'cash_bank' : String(raw['position_class'] ?? 'other'),
            detail: String(raw['detail_label'] ?? raw['accounting_date'] ?? 'Unassigned'),
            date: raw['accounting_date'] ? String(raw['accounting_date']) : undefined,
            balance: Number(raw['balance'] ?? 0),
            inbound: Number(raw['inbound'] ?? 0),
            outbound: Number(raw['outbound'] ?? 0),
          });
        }
        if (result.rows.length < pageSize) break;
        if (offset === 50000 - pageSize) throw new Error('The report has more than 50,000 detail rows; narrow the report before relying on totals.');
      }
      this.rows = rows;
    } catch (error) {
      if (request === this.generation) this.error = dashboardErrorMessage(error);
    } finally {
      if (request === this.generation) { this.loading = false; this.cdr.detectChanges(); }
    }
  }

  private query(): ReportQuery {
    const end = new Date(`${this.today}T12:00:00`);
    end.setDate(end.getDate() - 1);
    const where: Record<string, unknown>[] = [
      { type: 'condition', field: 'accounting_date', operator: 'lte', value: this.asOfDate },
    ];
    if (this.isBank) {
      where.push({ type: 'condition', field: 'bank_account_id', operator: 'eq', value: this.bankAccountId });
      where.push({ type: 'condition', field: 'position_class', operator: 'eq', value: 'cash_bank' });
    }
    const dimensions = this.isMovement ? ['accounting_date'] : ['position_class', 'detail_label'];
    return {
      version: 1, source: 'finance.asof-posted-items', from: end.toISOString(), duration: 3,
      select: [
        ...dimensions.map(field => ({ type: 'field', field, as: field })),
        { type: 'aggregate', function: 'sum', field: 'balance', as: 'balance' },
        { type: 'aggregate', function: 'sum', field: 'debit', as: 'inbound' },
        { type: 'aggregate', function: 'sum', field: 'credit', as: 'outbound' },
      ],
      group_by: dimensions.map(field => ({ field })),
      where: { type: 'group', operator: 'and', children: where },
      order_by: [{ ref: dimensions[0], direction: this.isMovement ? 'desc' : 'asc' }],
      limit: 200,
    };
  }
}

function dateOnly(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function classLabel(value: string): string {
  const labels: Record<string, string> = {
    cash_bank: 'Cash in bank', cash_branch: 'Cash in hand by branch',
    asset_receivable: 'Customers owe us', liability_payable: 'We owe vendors',
    asset_fixed: 'Fixed assets', asset_non_current: 'Other long-term assets',
    asset_current: 'Current assets and inventory', asset_prepayments: 'Prepayments',
    liability_current: 'Current liabilities', liability_non_current: 'Long-term liabilities',
    income: 'Revenue', income_other: 'Other income', expense: 'Expenses',
    equity: 'Equity', equity_unaffected: 'Opening equity',
  };
  if (value.startsWith('tax_')) return `Tax account · ${value.slice(4).replaceAll('_', ' ')}`;
  return labels[value] ?? value.replaceAll('_', ' ');
}
